import { useState, type JSX } from "react"
import { Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useCreditsSummary } from "../../redux/creditsSlice"
import { useTopup, useTopups } from "../../redux/topupsSlice"
import Spinner from "../widgets/Spinner"
import { TopupOrigin } from "../../api/models"

const originLabels: Record<string, string> = {
  order: "Store Order",
  subscription: "Subscription",
  code: "Code Redemption",
  grant: "Grant",
  reward: "Points Reward",
  free_claim: "Free Claim",
}

function periodEnd(period: SummaryPeriod): Date | null {
  if (period === "all") return null
  const now = new Date()
  if (period === "month") return new Date(now.getFullYear(), now.getMonth() + 1, now.getDate())
  return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
}

function TopupRow({ id, accessToken, summaryPeriod }: { id: string; accessToken: string; summaryPeriod: SummaryPeriod }) {
  const { data: topup } = useTopup(id, accessToken)
  if (!topup) return <tr><td colSpan={4}><Spinner size={24} /></td></tr>
  const expired = topup.expired

  const expiring = !expired && topup.expiresAt != null && (summaryPeriod === "all"  || (() => {
    const end = periodEnd(summaryPeriod)
    return end != null && new Date(topup.expiresAt!) <= end
  })())

  let originCell: JSX.Element
  switch (topup.origin) {
    case TopupOrigin.Order:
      originCell = <td><Link to={`/orders/${topup.refId}`} className="card-link">{originLabels[topup.origin]}</Link></td>
      break
    case TopupOrigin.CodeRedemption:
      originCell = <td><Link to={`/redemptions/${topup.refId}`} className="card-link">{originLabels[topup.origin]}</Link></td>
      break
    default:
      originCell = <td>{originLabels[topup.origin] ?? topup.origin}</td>
      break
  }

  const expiryStyle = expired
    ? { color: "var(--nfa-orange)" }
    : expiring
      ? { color: "var(--nfa-yellow)" }
      : undefined

  return (
    <tr>
      <td>{new Date(topup.createdAt).toLocaleDateString()}</td>
      { originCell }
      <td style={expiryStyle}>{topup.expiresAt ? new Date(topup.expiresAt).toLocaleDateString() : "—"}{expired ? <span className="badge-expired">EXPIRED</span> : null}</td>
      <td className="text-right topup-amount" style={expired ? { color: "var(--nfa-orange, #e53e3e)", textDecoration: "line-through" } : expiring ? { color: "var(--nfa-yellow)" } : undefined}>+{(topup.amount / 1e9).toFixed(2)}</td>
    </tr>
  )
}

type SummaryPeriod = "month" | "year" | "all"

export default function CreditsPage() {
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>("month")
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const { ids: topupIds, haveMore, loading, more } = useTopups(accessToken)
  const { data: summary } = useCreditsSummary(accessToken)

  return (
    <div className="page">
      <h1 className="page-title">Credits</h1>

      <div className="credits-hero">
        <span className="credits-balance">{summary ? `${(summary.available / 1e9).toFixed(2)}` : <Spinner size={24} />}</span>
        <span className="credits-label">credits available</span>
        <Link to="/store" className="card-link" style={{ marginTop: 8 }}>
          Buy more credits
        </Link>
      </div>

      <div className="summary-period-tabs">
        {([["month", "1m"], ["year", "1y"], ["all", "All"]] as const).map(([key, label]) => (
          <button
            key={key}
            className={`history-tab${summaryPeriod === key ? " history-tab-active" : ""}`}
            onClick={() => setSummaryPeriod(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="activity-summary">
        {summary ? (
          <>
            <div className="activity-stat">
              <span className="activity-value activity-received">{(summary.topups.deposited[summaryPeriod] / 1e9).toFixed(2)}</span>
              <span className="activity-label">deposited</span>
            </div>
            <div className="activity-stat">
              <span className="activity-value activity-spent">{(summary.spendings[summaryPeriod] / 1e9).toFixed(2)}</span>
              <span className="activity-label">spent</span>
            </div>
            <div className="activity-stat">
              <span className="activity-value activity-expired">{(summary.topups.expired[summaryPeriod] / 1e9).toFixed(2)}</span>
              <span className="activity-label">expired</span>
            </div>
            <div className="activity-divider" />
            <div className="activity-stat">
              <span className="activity-value activity-expiring">{(summary.topups.expiring[summaryPeriod] / 1e9).toFixed(2)}</span>
              <span className="activity-label">expiring</span>
            </div>
          </>
        ) : (
          <Spinner size={24} />
        )}
      </div>

      <h2 className="section-title">Top-up History</h2>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Origin</th>
              <th>Expires</th>
              <th>Credits</th>
            </tr>
          </thead>
          <tbody>
            {topupIds === undefined ? (
              <tr><td colSpan={4}><Spinner size={16} /></td></tr>
            ) : topupIds.length === 0 ? (
              <tr><td colSpan={4}>No top-ups yet</td></tr>
            ) : (
              topupIds.map((id) => (
                <TopupRow key={id} id={id} accessToken={accessToken} summaryPeriod={summaryPeriod} />
              ))
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginLeft: "8px" }}>
        {haveMore && topupIds !== undefined && (
          loading ? <Spinner size={16} /> : (
            <button className="card-link" onClick={more} style={{ marginLeft: "8", background: "none", border: "none", cursor: "pointer" }}>
              Load more
            </button>
          )
        )}
      </div>
    </div>
  )
}
